import {
  BOOK_CHAT_TOOL_FILTER,
  type ChatHeaderProps,
  ChatPanel,
  type EmptyStateAction,
  EmptyStateWithActions,
  useChat,
} from "@qw/chat";
import { MCPPlatform } from "@qw/qw-common";
import { Icon } from "@qw/ui";
import { useCallback, useMemo } from "react";

import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";

import { useChatResourceLinkHandlers } from "./use-chat-resource-link-handlers";

const BOOK_CHAT_MODE = "book-chat";

/**
 * The metadata that identifies a broker's book chat, used both to list those
 * sessions and to create one. Shared with the nav's Alby menu, which creates
 * them from outside this tree: a session minted under metadata the panel's own
 * filter does not match would never appear in the list, and the provider would
 * resolve to `create` again on the next render.
 */
export const bookChatSessionsFilter = (brokerId: string) => ({
  metadata: [
    { key: "brokerId", value: brokerId },
    { key: "mode", value: BOOK_CHAT_MODE },
  ],
});

interface BookChatPanelProps {
  brokerId: string;
  header?: ChatHeaderProps;
}

export const BookChatPanel = ({ brokerId, header }: BookChatPanelProps) => {
  const resourceLinkHandlers = useChatResourceLinkHandlers();
  const { chatSessionId, setChatSessionId } = useInboxQueryStates();

  // URL is the single source of truth for the active session. The chat
  // provider runs in *controlled* mode: `selectedSessionId={chatSessionId}`
  // feeds the URL in, `onSelectSession` writes it back. There's no internal
  // selection state to drift from the URL, so a broker already on /inbox can
  // click a second awaiting-input dropdown row (URL flips) and the panel
  // re-targets purely by derivation. `onSelectSession` fires for manual
  // selector picks, the first-load default adopt, and auto-create
  // completions — all persist to the URL. `history: "replace"` keeps these
  // out of the browser back stack so the back button leaves /inbox rather
  // than stepping through in-page selections.
  const handleSelectSession = useCallback(
    (next: { id: string } | null) => {
      setChatSessionId(next?.id ?? null, { history: "replace" });
    },
    [setChatSessionId]
  );

  const chatSessionsFilter = useMemo(() => bookChatSessionsFilter(brokerId), [brokerId]);
  const defaultPromptInput = useMemo(() => ({ mode: BOOK_CHAT_MODE, brokerId }), [brokerId]);

  return (
    <ChatPanel
      apiUrl={import.meta.env.VITE_REACT_APP_API_PATH}
      chatSessionsFilter={chatSessionsFilter}
      className="bg-card"
      compactActivePlaybooks
      defaultPromptInput={defaultPromptInput}
      emptyComponent={<BookChatEmptyState />}
      header={header}
      onSelectSession={handleSelectSession}
      platform={MCPPlatform.INBOX}
      selectedSessionId={chatSessionId}
      resourceLinkHandlers={resourceLinkHandlers}
      toolFilter={BOOK_CHAT_TOOL_FILTER}
      toolFilterStorageKey={`book-chat-${brokerId}`}
      supportsMultipleSessions
    />
  );
};

const BookChatEmptyState = () => {
  const { sendMessage } = useChat();
  const actions = useMemo<EmptyStateAction[]>(
    () => [
      {
        icon: <Icon icon="analytics" />,
        label: "Summarize my book",
        onClick: () => sendMessage({ text: "summarize my book" }),
      },
      {
        icon: <Icon icon="hourglass_top" />,
        label: "Find blockers",
        onClick: () => sendMessage({ text: "which accounts in my book need attention?" }),
      },
      {
        icon: <Icon icon="event_upcoming" />,
        label: "Upcoming renewals",
        onClick: () => sendMessage({ text: "show my upcoming renewals" }),
      },
      {
        icon: <Icon icon="draft" />,
        label: "Drafts to review",
        onClick: () => sendMessage({ text: "show drafts that need my review" }),
      },
      {
        icon: <Icon icon="link_off" />,
        label: "Unlinked threads",
        onClick: () => sendMessage({ text: "show unlinked inbox threads" }),
      },
    ],
    [sendMessage]
  );

  return (
    <EmptyStateWithActions
      actions={actions}
      className="justify-end pb-3.5"
      description="Ask me about open opportunities, renewals, drafts, and inbox threads."
    />
  );
};
